import { Controller } from '@hotwired/stimulus'

const pendingRecaptchaMounts = []

function visibleRecaptchaForm() {
  const forms = [...document.querySelectorAll('.access-recaptcha')]
    .map((node) => node.closest('form'))
    .filter(Boolean)
  return forms.find((form) => !form.closest('[hidden]')) || forms[0]
}

window.accessMountRecaptcha = () => {
  pendingRecaptchaMounts.splice(0).forEach((pending) => {
    pending.controller.mountRecaptcha(pending.mount, pending.sitekey)
  })
}

export default class extends Controller {
  static targets = ['password', 'toggle', 'signInPanel', 'createPanel', 'signInTab', 'createTab', 'submit', 'family',
    'campusPanel', 'credentials', 'securityPanel', 'securityEmail', 'recaptcha', 'securityWait', 'verified']

  connect() {
    if (this.hasSubmitTarget && this.submitTarget.disabled) {
      const buttons = () => document.querySelectorAll('[data-access-auth-target="submit"]')
      // The inline checkbox submits itself, the same way the security panel does
      // once its box is ticked. The label stays "Complete the check above".
      window.accessRecaptchaPassed = () => {
        const form = visibleRecaptchaForm()
        buttons().forEach((button) => {
          button.disabled = false
        })
        form?.requestSubmit()
      }
      window.accessRecaptchaExpired = () => {
        buttons().forEach((button) => {
          button.disabled = true
        })
      }
    }

    this.revealCampusespGoogle()
    if (this.element.parentElement?.closest('[data-controller~="access-auth"]')) return
    if (sessionStorage.getItem('access-panel-enter') !== '1') return

    sessionStorage.removeItem('access-panel-enter')
    const panel = this.element.classList.contains('access-panel') ? this.element : this.visiblePanel()
    this.playEnter(panel)
  }

  revealCampusespGoogle(event) {
    const input = event?.target?.matches?.('input') ? event.target : this.element.querySelector('#access-sign-in-email')
    if (!input) return

    const show = /^[^@\s]+@campusesp\.com$/i.test(input.value.trim())
    const root = this.element.closest('.access-panels') || this.element
    root.querySelectorAll('[data-campusesp-google]').forEach((element) => {
      element.hidden = !show
    })
  }

  togglePassword(event) {
    const button = event.currentTarget
    const input = button.parentElement.querySelector('input')
    if (!input) return

    const revealed = input.type === 'text'
    input.type = revealed ? 'password' : 'text'
    const verb = revealed ? 'Show' : 'Hide'
    button.textContent = verb
    button.setAttribute('aria-pressed', revealed ? 'false' : 'true')
    const field = button.dataset.passwordLabel || 'password'
    button.setAttribute('aria-label', `${verb} ${field}`)
  }

  showSignIn(event) {
    event.preventDefault()
    this.showPanel(false)
    window.history.replaceState({}, '', '/account?panel=sign_in')
  }

  showCreate(event) {
    event.preventDefault()
    this.showPanel(true)
    window.history.replaceState({}, '', '/account?panel=create')
  }

  showCampus(event) {
    event.preventDefault()
    this.switchRegion(this.familyTarget, this.campusPanelTarget)
  }

  showFamily(event) {
    event.preventDefault()
    this.switchRegion(this.campusPanelTarget, this.familyTarget)
  }

  leaveForPassword(event) {
    event.preventDefault()
    const href = event.currentTarget.href
    const panel = event.currentTarget.closest('.access-panel')
    this.finishLeave(panel).then(() => {
      sessionStorage.setItem('access-panel-enter', '1')
      window.location.assign(href)
    })
  }

  showPanel(create) {
    this.signInTabTarget.setAttribute('aria-selected', String(!create))
    this.createTabTarget.setAttribute('aria-selected', String(create))

    const current = create ? this.signInPanelTarget : this.createPanelTarget
    const next = create ? this.createPanelTarget : this.signInPanelTarget
    if (!next.hidden || this.switching) return

    this.switching = true
    this.finishLeave(current).then(() => {
      current.hidden = true
      current.classList.remove('is-leaving')
      next.hidden = false
      this.playEnter(next)
      this.focusFirstField(next)
      this.switching = false
    })
  }

  switchRegion(current, next) {
    if (!current || !next || !next.hidden || this.switching) return

    this.switching = true
    this.finishLeave(current).then(() => {
      current.hidden = true
      current.classList.remove('is-leaving')
      next.hidden = false
      this.playEnter(next)
      this.focusHeading(next)
      this.switching = false
    })
  }

  visiblePanel() {
    if (this.hasSignInPanelTarget && !this.signInPanelTarget.hidden) return this.signInPanelTarget
    if (this.hasCreatePanelTarget && !this.createPanelTarget.hidden) return this.createPanelTarget
    return null
  }

  finishLeave(panel) {
    if (!panel) return Promise.resolve()

    panel.classList.add('is-leaving')
    return new Promise((resolve) => {
      let settled = false
      const done = () => {
        if (settled) return
        settled = true
        panel.removeEventListener('animationend', done)
        resolve()
      }
      panel.addEventListener('animationend', done)
      window.setTimeout(done, 200)
    })
  }

  probeSignIn(event) {
    this.probe(event, 'X-Account-Sign-In')
  }

  probeSignUp(event) {
    this.probe(event, 'X-Account-Sign-Up')
  }

  probePassword(event) {
    this.probe(event, 'X-Account-Password')
  }

  closeSecurity(event) {
    event.preventDefault()
    this.allowNativeSubmit = false
    this.showCredentials()
  }

  probe(event, headerName) {
    if (this.allowNativeSubmit || !this.hasSecurityPanelTarget) return
    if (this.wafNeedsToken()) return
    if (this.hasSubmitTarget && this.submitTarget.disabled) return

    event.preventDefault()
    this.probeForm(event.target, headerName)
  }

  async probeForm(form, headerName) {
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': token,
          [headerName]: '1',
        },
        redirect: 'manual',
      })
      const payload = await response.json().catch(() => ({}))
      if (payload.step === 'security') {
        this.openSecurity()
        return
      }
      if (payload.step === 'mfa') {
        this.showMfa(payload)
        return
      }
      if (payload.redirect) {
        window.location.assign(payload.redirect)
        return
      }
    } catch {
      // A failed probe falls through to the normal post.
    }

    this.allowNativeSubmit = true
    form.requestSubmit()
  }

  showMfa(payload) {
    const root = this.element.closest('.access-panels') || this.element
    const panel = root.querySelector('[data-access-mfa]')
    if (!panel) return

    const credentials = root.querySelector('[data-access-auth-target="credentials"]')
    if (credentials) credentials.hidden = true
    root.querySelectorAll('.access-divider, .access-social-form, [data-access-mfa-companion]').forEach((node) => {
      node.hidden = true
    })
    const email = root.querySelector('#access-sign-in-email')?.value
    const identity = panel.querySelector('[data-login-code-target="email"]')
    if (identity && email) identity.textContent = email
    const phone = panel.querySelector('[data-login-code-target="phone"]')
    if (phone && payload.phone) phone.textContent = payload.phone
    panel.hidden = false
    this.focusHeading(panel)
    const code = this.application.getControllerForElementAndIdentifier(panel, 'login-code')
    code?.start(payload.resend_in, { focus: true })
  }

  backToSignIn(event) {
    event.preventDefault()
    const root = this.element.closest('.access-panels') || this.element
    const panel = root.querySelector('[data-access-mfa]')
    if (panel) panel.hidden = true
    const credentials = root.querySelector('[data-access-auth-target="credentials"]')
    if (credentials) credentials.hidden = false
    root.querySelectorAll('.access-divider, .access-social-form, [data-access-mfa-companion]').forEach((node) => {
      node.hidden = false
    })
    this.focusFirstField(credentials)
  }

  openSecurity() {
    const email = this.element.querySelector('input[name="user[email]"]')
    if (this.hasSecurityEmailTarget) this.securityEmailTarget.textContent = email?.value || ''
    if (this.hasCredentialsTarget) this.credentialsTarget.hidden = true
    this.securityPanelTarget.hidden = false
    if (this.hasSecurityWaitTarget) this.securityWaitTarget.hidden = false
    if (this.hasVerifiedTarget) this.verifiedTarget.hidden = true
    this.companions().forEach((node) => {
      node.hidden = true
    })
    this.renderRecaptcha()
    this.focusHeading(this.securityPanelTarget)
  }

  showCredentials() {
    if (this.hasSecurityPanelTarget) this.securityPanelTarget.hidden = true
    if (this.hasCredentialsTarget) this.credentialsTarget.hidden = false
    this.companions().forEach((node) => {
      node.hidden = false
    })
    this.focusFirstField(this.hasCredentialsTarget ? this.credentialsTarget : null)
  }

  focusHeading(root) {
    const heading = root?.querySelector('h1, h2')
    if (!heading) return

    heading.setAttribute('tabindex', '-1')
    heading.focus()
  }

  focusFirstField(root) {
    const field = root?.querySelector('input:not([type="hidden"]):not([tabindex="-1"])')
    field?.focus()
  }

  companions() {
    return this.element.closest('.access-panel')?.querySelectorAll('.access-divider, .access-social-form') || []
  }

  wafNeedsToken() {
    return window.AwsWafIntegration?.hasToken && !window.AwsWafIntegration.hasToken()
  }

  renderRecaptcha() {
    const mount = this.hasRecaptchaTarget ? this.recaptchaTarget : null
    if (!mount || mount.dataset.mounted) return

    const sitekey = mount.dataset.sitekey
    if (!sitekey) return
    if (window.grecaptcha?.render) {
      window.grecaptcha.ready(() => this.mountRecaptcha(mount, sitekey))
      return
    }

    pendingRecaptchaMounts.push({ controller: this, mount, sitekey })
    if (document.querySelector('script[data-access-recaptcha]')) return

    const script = document.createElement('script')
    script.src = 'https://www.google.com/recaptcha/api.js?onload=accessMountRecaptcha&render=explicit'
    script.async = true
    script.dataset.accessRecaptcha = '1'
    document.head.appendChild(script)
  }

  mountRecaptcha(mount, sitekey) {
    if (!mount || mount.dataset.mounted || !window.grecaptcha?.render) return

    mount.dataset.mounted = '1'
    window.grecaptcha.render(mount, {
      sitekey,
      callback: (token) => this.finishSecurityCheck(token),
      'expired-callback': () => this.expireSecurityCheck(),
    })
  }

  finishSecurityCheck(token) {
    const form = this.element.matches('form') ? this.element : this.element.querySelector('form')
    if (!form) return

    this.storeRecaptchaToken(form, token)
    if (this.hasSecurityWaitTarget) this.securityWaitTarget.hidden = true
    if (this.hasVerifiedTarget) this.verifiedTarget.hidden = false
    this.allowNativeSubmit = true
    form.requestSubmit()
  }

  storeRecaptchaToken(form, token) {
    let field = form.querySelector('textarea[name="g-recaptcha-response"]')
    if (!field) {
      field = document.createElement('textarea')
      field.name = 'g-recaptcha-response'
      field.hidden = true
      form.appendChild(field)
    }
    field.value = token
  }

  expireSecurityCheck() {
    if (this.hasVerifiedTarget) this.verifiedTarget.hidden = true
    if (this.hasSecurityWaitTarget) this.securityWaitTarget.hidden = false
    this.allowNativeSubmit = false
  }

  playEnter(panel) {
    if (!panel) return

    panel.classList.remove('is-leaving')
    panel.classList.add('is-entering')
    const done = () => {
      panel.classList.remove('is-entering')
      panel.removeEventListener('animationend', done)
    }
    panel.addEventListener('animationend', done)
  }
}
